import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Devices as DevicesIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ja';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import {
  useGetScheduledNotificationsQuery,
  useSendNotificationMutation,
  useCancelNotificationMutation,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useSearchUsersQuery,
} from '../../store/api/adminApi';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('ja');

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface UserOption {
  id: string;
  name: string;
  email: string;
  hasFcmToken?: boolean;
}

const NotificationsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [targetType, setTargetType] = useState<'all_devices' | 'all' | 'logged_in' | 'individual'>('all_devices');
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sendType, setSendType] = useState<'immediate' | 'scheduled'>('immediate');
  const [scheduledAt, setScheduledAt] = useState<Dayjs | null>(dayjs().add(1, 'hour'));
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [cancelDialog, setCancelDialog] = useState<{ open: boolean; notificationId: string | null }>({
    open: false,
    notificationId: null,
  });

  // API Hooks
  const { data: notificationsData, isLoading: isLoadingNotifications, refetch: refetchNotifications } = useGetScheduledNotificationsQuery({});
  const { data: searchResults, isLoading: isSearchingUsers, error: searchError } = useSearchUsersQuery(userSearchQuery, {
    skip: userSearchQuery.length < 1,
  });
  const { data: notificationSettings, isLoading: isLoadingSettings } = useGetNotificationSettingsQuery();

  // デバッグ用ログ
  useEffect(() => {
    console.log('[DEBUG] userSearchQuery:', userSearchQuery);
    console.log('[DEBUG] searchResults:', searchResults);
    console.log('[DEBUG] isSearchingUsers:', isSearchingUsers);
    console.log('[DEBUG] searchError:', searchError);
  }, [userSearchQuery, searchResults, isSearchingUsers, searchError]);
  const [sendNotification, { isLoading: isSending }] = useSendNotificationMutation();
  const [cancelNotification, { isLoading: isCancelling }] = useCancelNotificationMutation();
  const [updateSettings, { isLoading: isUpdatingSettings }] = useUpdateNotificationSettingsMutation();

  // 定期通知設定
  const [weeklyEnabled, setWeeklyEnabled] = useState(true);
  const [weeklyTitle, setWeeklyTitle] = useState('アリクイ');
  const [weeklyBody, setWeeklyBody] = useState('今週のおすすめ作品をチェックしよう！');

  useEffect(() => {
    if (notificationSettings) {
      setWeeklyEnabled(notificationSettings.weeklyNotificationEnabled ?? true);
      setWeeklyTitle(notificationSettings.weeklyNotificationTitle ?? 'アリクイ');
      setWeeklyBody(notificationSettings.weeklyNotificationBody ?? '今週のおすすめ作品をチェックしよう！');
    }
  }, [notificationSettings]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSendNotification = async () => {
    if (!title.trim() || !body.trim()) {
      setSnackbar({ open: true, message: 'タイトルとメッセージを入力してください', severity: 'error' });
      return;
    }

    if (targetType === 'individual' && selectedUsers.length === 0) {
      setSnackbar({ open: true, message: '送信先ユーザーを選択してください', severity: 'error' });
      return;
    }

    if (sendType === 'scheduled' && (!scheduledAt || scheduledAt.isBefore(dayjs()))) {
      setSnackbar({ open: true, message: '有効な送信日時を指定してください', severity: 'error' });
      return;
    }

    try {
      await sendNotification({
        title: title.trim(),
        body: body.trim(),
        targetType,
        targetUserIds: targetType === 'individual' ? selectedUsers.map(u => u.id) : undefined,
        scheduledAt: sendType === 'scheduled' ? scheduledAt?.toISOString() : undefined,
      }).unwrap();

      setSnackbar({
        open: true,
        message: sendType === 'immediate' ? '通知を送信しました' : '通知を予約しました',
        severity: 'success',
      });

      // フォームをリセット
      setTitle('');
      setBody('');
      setSelectedUsers([]);
      setScheduledAt(dayjs().add(1, 'hour'));
      refetchNotifications();

      // 送信後2分間はクールダウン（連続送信防止）
      if (sendType === 'immediate' && targetType !== 'individual') {
        setSendCooldown(true);
        setTimeout(() => setSendCooldown(false), 120_000);
      }
    } catch (error: any) {
      const errorMsg = error?.data?.error || error?.status === 409
        ? error?.data?.error || '同一内容の通知が直前に送信されています。しばらくお待ちください。'
        : '通知の送信に失敗しました';
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error',
      });
    }
  };

  const handleCancelNotification = async () => {
    if (!cancelDialog.notificationId) return;

    try {
      await cancelNotification(cancelDialog.notificationId).unwrap();
      setSnackbar({ open: true, message: '予約をキャンセルしました', severity: 'success' });
      setCancelDialog({ open: false, notificationId: null });
      refetchNotifications();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.data?.error || 'キャンセルに失敗しました',
        severity: 'error',
      });
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await updateSettings({
        weeklyNotificationEnabled: weeklyEnabled,
        weeklyNotificationTitle: weeklyTitle,
        weeklyNotificationBody: weeklyBody,
      }).unwrap();
      setSnackbar({ open: true, message: '設定を更新しました', severity: 'success' });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.data?.error || '設定の更新に失敗しました',
        severity: 'error',
      });
    }
  };

  const [confirmDialog, setConfirmDialog] = useState(false);
  const [sendCooldown, setSendCooldown] = useState(false);

  const FIREBASE_ENV = process.env.REACT_APP_FIREBASE_ENV || 'test';
  const IS_PRODUCTION = FIREBASE_ENV === 'production';

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip label="予約中" color="warning" size="small" icon={<ScheduleIcon />} />;
      case 'sending':
        return <Chip label="送信中" color="info" size="small" icon={<CircularProgress size={14} />} />;
      case 'sent':
        return <Chip label="送信済み" color="success" size="small" />;
      case 'failed':
        return <Chip label="失敗" color="error" size="small" />;
      case 'cancelled':
        return <Chip label="キャンセル" color="default" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return dayjs(dateString).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm');
  };

  // 次の土曜19時を計算
  const getNextSaturdayAt19 = () => {
    let next = dayjs().tz('Asia/Tokyo').day(6).hour(19).minute(0).second(0);
    if (next.isBefore(dayjs())) {
      next = next.add(1, 'week');
    }
    return next.format('YYYY年M月D日 HH:mm');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon /> プッシュ通知管理
          </Typography>
          <Chip
            label={IS_PRODUCTION ? '本番環境' : 'テスト環境'}
            color={IS_PRODUCTION ? 'error' : 'info'}
            variant="filled"
            size="medium"
            sx={{ fontWeight: 'bold', fontSize: '14px' }}
          />
        </Box>

        <Paper sx={{ width: '100%', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
            <Tab icon={<SendIcon />} label="通知送信" />
            <Tab icon={<HistoryIcon />} label="送信履歴" />
            <Tab icon={<SettingsIcon />} label="定期通知設定" />
          </Tabs>

          {/* 通知送信タブ */}
          <TabPanel value={tabValue} index={0}>
            <Box>
              <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      送信対象
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>送信対象</InputLabel>
                      <Select
                        value={targetType}
                        label="送信対象"
                        onChange={(e) => setTargetType(e.target.value as 'all_devices' | 'all' | 'logged_in' | 'individual')}
                      >
                        <MenuItem value="all_devices">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DevicesIcon /> 全デバイス（未ログインユーザー含む）
                          </Box>
                        </MenuItem>
                        <MenuItem value="all">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PeopleIcon /> ログインユーザー全員（アカウント登録済み）
                          </Box>
                        </MenuItem>
                        <MenuItem value="logged_in">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PeopleIcon /> アクティブユーザー（過去30日以内に使用）
                          </Box>
                        </MenuItem>
                        <MenuItem value="individual">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon /> 個別ユーザー
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>

                    {targetType === 'individual' && (
                      <Autocomplete
                        multiple
                        options={searchResults?.users || []}
                        getOptionLabel={(option) => `${option.name} (${option.email || option.id})`}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        value={selectedUsers}
                        onChange={(_, newValue) => setSelectedUsers(newValue)}
                        onInputChange={(_, value) => setUserSearchQuery(value)}
                        loading={isSearchingUsers}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">
                                {option.name}
                                {option.hasFcmToken === false && (
                                  <Chip label="トークンなし" size="small" color="warning" sx={{ ml: 1 }} />
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.email || 'メール未設定'} | ID: {option.id.substring(0, 12)}...
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="ユーザーを検索"
                            placeholder="名前、メールアドレス、またはユーザーIDで検索"
                            helperText="ユーザー名・メールアドレスの前方一致、またはユーザーIDの完全一致で検索できます"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isSearchingUsers ? <CircularProgress size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        sx={{ mb: 2 }}
                      />
                    )}

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                      通知内容
                    </Typography>
                    <TextField
                      fullWidth
                      label="タイトル"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      sx={{ mb: 2 }}
                      inputProps={{ maxLength: 50 }}
                      helperText={`${title.length}/50文字`}
                    />
                    <TextField
                      fullWidth
                      label="メッセージ"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      multiline
                      rows={3}
                      sx={{ mb: 2 }}
                      inputProps={{ maxLength: 200 }}
                      helperText={`${body.length}/200文字`}
                    />

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                      送信タイミング
                    </Typography>
                    <FormControl component="fieldset" sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant={sendType === 'immediate' ? 'contained' : 'outlined'}
                          onClick={() => setSendType('immediate')}
                          startIcon={<SendIcon />}
                        >
                          即時送信
                        </Button>
                        <Button
                          variant={sendType === 'scheduled' ? 'contained' : 'outlined'}
                          onClick={() => setSendType('scheduled')}
                          startIcon={<ScheduleIcon />}
                        >
                          予約送信
                        </Button>
                      </Box>
                    </FormControl>

                    {sendType === 'scheduled' && (
                      <Box sx={{ mb: 2 }}>
                        <DateTimePicker
                          label="送信日時（日本時間）"
                          value={scheduledAt}
                          onChange={(newValue) => setScheduledAt(newValue)}
                          minDateTime={dayjs()}
                          ampm={false}
                          format="YYYY/MM/DD HH:mm"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              helperText: '※5分単位で送信されます',
                            },
                          }}
                        />
                      </Box>
                    )}

                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          if (!title.trim() || !body.trim()) {
                            handleSendNotification();
                            return;
                          }
                          setConfirmDialog(true);
                        }}
                        disabled={isSending || sendCooldown}
                        startIcon={isSending ? <CircularProgress size={20} /> : <SendIcon />}
                        size="large"
                      >
                        {isSending ? '送信処理中...' : sendCooldown ? '送信済み（クールダウン中）' : sendType === 'immediate' ? '送信する' : '予約する'}
                      </Button>
                      {isSending && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          大量のデバイスに送信中です。このまま画面を閉じないでください。
                        </Typography>
                      )}
                      {sendCooldown && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                          通知が正常に送信されました。重複防止のため、2分間は同じ送信先への再送信ができません。
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
            </Box>
          </TabPanel>

          {/* 送信履歴タブ */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">送信履歴</Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => refetchNotifications()}
                disabled={isLoadingNotifications}
              >
                更新
              </Button>
            </Box>

            {isLoadingNotifications ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>タイトル</TableCell>
                      <TableCell>対象</TableCell>
                      <TableCell>ステータス</TableCell>
                      <TableCell>予定/送信日時</TableCell>
                      <TableCell>送信数</TableCell>
                      <TableCell>操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notificationsData?.notifications?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          送信履歴はありません
                        </TableCell>
                      </TableRow>
                    ) : (
                      notificationsData?.notifications?.map((notification: any) => (
                        <TableRow key={notification.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {notification.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {notification.body?.substring(0, 30)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {notification.targetType === 'all_devices' ? (
                              <Chip label="全デバイス" size="small" icon={<DevicesIcon />} color="secondary" />
                            ) : notification.targetType === 'all' ? (
                              <Chip label="全ユーザー" size="small" icon={<PeopleIcon />} />
                            ) : notification.targetType === 'logged_in' ? (
                              <Chip label="アクティブユーザー" size="small" icon={<PeopleIcon />} color="primary" />
                            ) : (
                              <Chip label={`${notification.targetUserIds?.length || 0}人`} size="small" icon={<PersonIcon />} />
                            )}
                          </TableCell>
                          <TableCell>{getStatusChip(notification.status)}</TableCell>
                          <TableCell>
                            {notification.status === 'sent'
                              ? formatDateTime(notification.sentAt)
                              : formatDateTime(notification.scheduledAt)}
                          </TableCell>
                          <TableCell>
                            {notification.sentCount !== undefined ? (
                              <Typography variant="body2">
                                {notification.sentCount} / {notification.sentCount + (notification.failedCount || 0)}
                              </Typography>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            {notification.status === 'pending' && (
                              <Tooltip title="予約をキャンセル">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => setCancelDialog({ open: true, notificationId: notification.id })}
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* 定期通知設定タブ */}
          <TabPanel value={tabValue} index={2}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  週次おすすめ通知
                </Typography>
                <Alert severity="info" sx={{ mb: 3 }}>
                  毎週土曜日 19:00（日本時間）に、ログイン有無に関わらず全ユーザー（通知許可済み）にプッシュ通知を送信します。
                  <br />
                  <strong>次回送信予定:</strong> {getNextSaturdayAt19()}
                </Alert>

                <FormControlLabel
                  control={
                    <Switch
                      checked={weeklyEnabled}
                      onChange={(e) => setWeeklyEnabled(e.target.checked)}
                    />
                  }
                  label="週次通知を有効にする"
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="通知タイトル"
                  value={weeklyTitle}
                  onChange={(e) => setWeeklyTitle(e.target.value)}
                  disabled={!weeklyEnabled}
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 50 }}
                />

                <TextField
                  fullWidth
                  label="通知メッセージ"
                  value={weeklyBody}
                  onChange={(e) => setWeeklyBody(e.target.value)}
                  disabled={!weeklyEnabled}
                  multiline
                  rows={3}
                  sx={{ mb: 3 }}
                  inputProps={{ maxLength: 200 }}
                />

                <Button
                  variant="contained"
                  onClick={handleUpdateSettings}
                  disabled={isUpdatingSettings || isLoadingSettings}
                  startIcon={isUpdatingSettings ? <CircularProgress size={20} /> : null}
                >
                  設定を保存
                </Button>
              </CardContent>
            </Card>
          </TabPanel>
        </Paper>

        {/* 送信確認ダイアログ */}
        <Dialog open={confirmDialog} onClose={() => !isSending && setConfirmDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: IS_PRODUCTION ? '#d32f2f' : '#1565c0', color: '#fff' }}>
            {IS_PRODUCTION ? '⚠ 本番環境' : 'テスト環境'}で通知を{sendType === 'immediate' ? '送信' : '予約'}しますか？
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {IS_PRODUCTION && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>本番環境</strong>から送信します。全ユーザーの端末にプッシュ通知が配信されます。
                送信後の取り消しはできません。
              </Alert>
            )}
            <Box sx={{
              p: 2, mb: 2, borderRadius: 1,
              bgcolor: IS_PRODUCTION ? '#fff3e0' : '#e3f2fd',
              border: `1px solid ${IS_PRODUCTION ? '#ff9800' : '#90caf9'}`,
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                接続先: {IS_PRODUCTION ? '本番環境 (art-flea-9c326)' : 'テスト環境 (art-flea-test)'}
              </Typography>
            </Box>
            <Typography variant="body2" gutterBottom>
              <strong>送信対象:</strong>{' '}
              {targetType === 'all_devices' ? '全デバイス（未ログインユーザー含む）' :
               targetType === 'all' ? 'ログインユーザー全員' :
               targetType === 'logged_in' ? 'アクティブユーザー（過去30日）' :
               `個別ユーザー (${selectedUsers.length}人)`}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>タイトル:</strong> {title}
            </Typography>
            <Typography variant="body2">
              <strong>メッセージ:</strong> {body}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(false)} disabled={isSending}>
              キャンセル
            </Button>
            <Button
              onClick={() => {
                setConfirmDialog(false);
                handleSendNotification();
              }}
              color={IS_PRODUCTION ? 'error' : 'primary'}
              variant="contained"
              disabled={isSending || sendCooldown}
            >
              {IS_PRODUCTION ? '本番環境に' : ''}{sendType === 'immediate' ? '送信する' : '予約する'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* キャンセル確認ダイアログ */}
        <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, notificationId: null })}>
          <DialogTitle>予約をキャンセル</DialogTitle>
          <DialogContent>
            この予約通知をキャンセルしてもよろしいですか？
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialog({ open: false, notificationId: null })}>
              戻る
            </Button>
            <Button
              onClick={handleCancelNotification}
              color="error"
              disabled={isCancelling}
            >
              {isCancelling ? <CircularProgress size={20} /> : 'キャンセルする'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* スナックバー */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default NotificationsPage;

